import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
    Trash2, 
    Check, 
    X, 
    Loader2, 
    ArrowUpDown,
    Search,
    Filter,
    ChevronDown,
    ChevronRight
} from 'lucide-react';
import ProjectHeader from '@/components/header';
import { ProjectService } from '@/services/ProjectService';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
// import { toast } from "@/components/ui/use-toast";

const AdminProjects = () => {
    const navigate = useNavigate();
    const [collections, setCollections] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
    const [statusFilter, setStatusFilter] = useState('all');
    const [expandedCollections, setExpandedCollections] = useState({});

    useEffect(() => {
        fetchCollections();
    }, []);

    async function fetchCollections() {
        try {
            setIsLoading(true);
            const businessCollections = await ProjectService.getBusinessCollections();
            console.log('Received collections:', businessCollections);
            if (Array.isArray(businessCollections)) {
                setCollections(businessCollections);
            } else {
                console.error('Received invalid collections data:', businessCollections);
                // Keep existing collections if new fetch fails
                setCollections(prev => prev);
            }
        } catch (error) {
            console.error('Error fetching business collections:', error);
            // Keep existing collections if fetch fails
            setCollections(prev => prev);
        } finally {
            setIsLoading(false);
        }
    }

    const handleSort = (key) => {
        setSortConfig(current => ({
            key,
            direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const toggleExpandCollection = (collectionId) => {
        setExpandedCollections(prev => ({
            ...prev,
            [collectionId]: !prev[collectionId]
        }));
    };

    const handleDeleteProject = async (collectionId, projectId) => {
        if (window.confirm('Are you sure you want to delete this project?')) {
            try {
                await ProjectService.removeProjectFromCollection(collectionId, projectId);
                
                // Update the local state to reflect the deletion
                setCollections(prev => 
                    prev.map(collection => {
                        if (collection.id === collectionId) {
                            return {
                                ...collection,
                                projectIds: collection.projectIds.filter(id => id !== projectId),
                                projects: collection.projects.filter(project => project.id !== projectId)
                            };
                        }
                        return collection;
                    })
                );
                
                // toast({
                //     title: "Project deleted",
                //     description: "The project has been successfully removed.",
                //     status: "success"
                // });
            } catch (error) {
                console.error('Failed to delete project:', error);
                // toast({
                //     title: "Error",
                //     description: "Failed to delete the project. Please try again.",
                //     status: "error"
                // });
            }
        }
    };

    const sortedCollections = React.useMemo(() => {
        let filteredCollections = [...collections];
        
        // Apply search filter
        if (searchTerm) {
            filteredCollections = filteredCollections.filter(collection => 
                collection.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                collection.ContactDetails?.contactEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                collection.ContactDetails?.contactName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                // Search in project details if available
                collection.projectDetails?.street1?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                collection.projectDetails?.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                collection.projectDetails?.state?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                collection.projectDetails?.zipCode?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Apply status filter
        if (statusFilter !== 'all') {
            filteredCollections = filteredCollections.filter(collection => 
                collection.status === statusFilter
            );
        }

        // Apply sorting
        return filteredCollections.sort((a, b) => {
            if (sortConfig.key === 'createdAt') {
                return sortConfig.direction === 'asc' 
                    ? parseInt(a.createdAt) - parseInt(b.createdAt)
                    : parseInt(b.createdAt) - parseInt(a.createdAt);
            }

            if (sortConfig.key === 'contactDetails') {
                // Default to sorting by contactName for contactDetails column
                const aName = a.ContactDetails?.contactName || '';
                const bName = b.ContactDetails?.contactName || '';
                return sortConfig.direction === 'asc' 
                    ? aName.localeCompare(bName)
                    : bName.localeCompare(aName);
            }
            
            if (a[sortConfig.key] < b[sortConfig.key]) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (a[sortConfig.key] > b[sortConfig.key]) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }, [collections, sortConfig, searchTerm, statusFilter]);

    const handleDeleteCollection = async (collectionId) => {
        if (window.confirm('Are you sure you want to delete this collection?')) {
            try {
                await ProjectService.deleteCollection(collectionId);
                setCollections(collections.filter(collection => collection.id !== collectionId));
                // toast({
                //     title: "Collection deleted",
                //     description: "The collection and its projects have been successfully deleted.",
                //     status: "success"
                // });
            } catch (error) {
                console.error('Failed to delete collection:', error);
                // toast({
                //     title: "Error",
                //     description: "Failed to delete the collection. Please try again.",
                //     status: "error"
                // });
            }
        }
    };

    const SortButton = ({ column, children }) => (
        <Button
            variant="ghost"
            onClick={() => handleSort(column)}
            className="h-8 flex items-center gap-1 hover:bg-gray-100"
        >
            {children}
            <ArrowUpDown className="h-4 w-4" />
        </Button>
    );

    const getStatusBadgeClass = (status) => {
        return status === 'submitted' 
            ? 'bg-green-100 text-green-800'
            : 'bg-blue-100 text-blue-800';
    };

    const formatDate = (timestamp) => {
        return new Date(parseInt(timestamp)).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-100 p-6">
                <ProjectHeader showHomeButton={true} />
                <main className="pt-20 flex justify-center">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                </main>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100 p-6">
            <ProjectHeader showHomeButton={true} />

            <main className="pt-20 px-4">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-semibold">All Projects</h2>
                    <div className="flex gap-4">
                        <div className="relative">
                            <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
                            <Input
                                placeholder="Search projects..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-8 w-64"
                            />
                        </div>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="outline" className="gap-2">
                                    <Filter className="h-4 w-4" />
                                    Status
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                                <DropdownMenuItem onClick={() => setStatusFilter('all')}>
                                    All
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setStatusFilter('submitted')}>
                                    Submitted
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setStatusFilter('in_progress')}>
                                    In Progress
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-10"></TableHead>
                                <TableHead><SortButton column="name">Project Name</SortButton></TableHead>
                                <TableHead><SortButton column="contactDetails">Contact Details</SortButton></TableHead>
                                <TableHead><SortButton column="createdAt">Created</SortButton></TableHead>
                                <TableHead><SortButton column="status">Status</SortButton></TableHead>
                                <TableHead>Project Details</TableHead>
                                <TableHead className="text-center">Number of Rooms</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {sortedCollections.map(collection => (
                                <React.Fragment key={collection.id}>
                                    <TableRow className="cursor-pointer hover:bg-gray-50">
                                        <TableCell onClick={() => toggleExpandCollection(collection.id)}>
                                            {expandedCollections[collection.id] ? (
                                                <ChevronDown className="h-5 w-5 text-gray-500" />
                                            ) : (
                                                <ChevronRight className="h-5 w-5 text-gray-500" />
                                            )}
                                        </TableCell>
                                        <TableCell className="font-medium">{collection.name}</TableCell>
                                        <TableCell>
                                            {collection.ContactDetails ? (
                                                <div className="space-y-1 text-sm">
                                                    {collection.ContactDetails.contactName && (
                                                        <div><span className="font-medium">Name:</span> {collection.ContactDetails.contactName}</div>
                                                    )}
                                                    {collection.ContactDetails.contactEmail && (
                                                        <div><span className="font-medium">Email:</span> {collection.ContactDetails.contactEmail}</div>
                                                    )}
                                                    {collection.ContactDetails.projectFor && typeof collection.ContactDetails.projectFor === 'object' && (
                                                        <div>
                                                            <span className="font-medium">Project For:</span>
                                                            {collection.ContactDetails.projectFor.name && <div><span className="font-medium">Customer Name:</span> {collection.ContactDetails.projectFor.name}</div>}
                                                            {collection.ContactDetails.projectFor.email && <div><span className="font-medium">Customer Email:</span> {collection.ContactDetails.projectFor.email}</div>}
                                                        </div>
                                                    )}
                                                    {collection.ContactDetails.phone && (
                                                        <div><span className="font-medium">Phone:</span> {collection.ContactDetails.phone}</div>
                                                    )}
                                                    {collection.ContactDetails.fields && Object.entries(collection.ContactDetails.fields).map(([key, field]) => (
                                                        field && field.question && (
                                                            <div key={key}>
                                                                <span className="font-medium">{field.question}:</span> {field.answer || '-'}
                                                            </div>
                                                        )
                                                    ))}
                                                </div>
                                            ) : '-'}
                                        </TableCell>
                                        <TableCell>
                                            {formatDate(collection.createdAt)}
                                        </TableCell>
                                        <TableCell>
                                            <span className={`px-2 py-1 rounded-full text-sm ${getStatusBadgeClass(collection.status)}`}>
                                                {collection.status === 'submitted' ? 'Submitted' : 'In Progress'}
                                            </span>
                                        </TableCell>
                                        <TableCell>
                                            {collection.projectDetails ? (
                                                <div className="space-y-1 text-sm">
                                                    {collection.projectDetails.street1 && (
                                                        <div>
                                                            <span className="font-medium">Address:</span> {collection.projectDetails.street1}
                                                            {collection.projectDetails.street2 && `, ${collection.projectDetails.street2}`}
                                                        </div>
                                                    )}
                                                    {(collection.projectDetails.city || collection.projectDetails.state || collection.projectDetails.zipCode) && (
                                                        <div>
                                                            <span className="font-medium">Location:</span>
                                                            {' '}
                                                            {collection.projectDetails.city && `city: ${collection.projectDetails.city}, `}
                                                            {collection.projectDetails.state && `state: ${collection.projectDetails.state} `}
                                                            {collection.projectDetails.zipCode && `zip: ${collection.projectDetails.zipCode}`}
                                                        </div>
                                                    )}
                                                    {/* Display other project details fields if they exist */}
                                                    {collection.projectDetails.fields && Object.entries(collection.projectDetails.fields).map(([key, field]) => (
                                                        field && field.question && (
                                                            <div key={key}>
                                                                <span className="font-medium">{field.question}:</span> {field.answer || '-'}
                                                            </div>
                                                        )
                                                    ))}
                                                </div>
                                            ) : '-'}
                                        </TableCell>
                                        <TableCell className="text-center">
                                            {collection.projects ? collection.projects.length : (collection.projectIds ? collection.projectIds.length : 0)}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-2">
                                                <Button
                                                    onClick={() => {
                                                        const navigatePath = `/collection/${collection.id}/summary`;
                                                        console.log('Opening in new tab:', navigatePath);
                                                        window.open(navigatePath, '_blank');
                                                    }}
                                                    variant="outline"
                                                    size="sm"
                                                >
                                                    View
                                                </Button>
                                                <Button
                                                    onClick={() => handleDeleteCollection(collection.id)}
                                                    variant="destructive"
                                                    size="sm"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                    
                                    {/* Expandable row for projects */}
                                    {expandedCollections[collection.id] && collection.projects && collection.projects.length > 0 && (
                                        <TableRow className="bg-gray-50">
                                            <TableCell colSpan={8} className="p-0">
                                                <div className="p-4">
                                                    <h3 className="font-medium text-lg mb-3">Rooms in this Project</h3>
                                                    <Table>
                                                        <TableHeader>
                                                            <TableRow>
                                                                <TableHead>Name</TableHead>
                                                                <TableHead>Type</TableHead>
                                                                <TableHead>Status</TableHead>
                                                                <TableHead>Created</TableHead>
                                                                <TableHead>Updated</TableHead>
                                                                <TableHead className="text-right">Actions</TableHead>
                                                            </TableRow>
                                                        </TableHeader>
                                                        <TableBody>
                                                            {collection.projects.map(project => (
                                                                <TableRow key={project.id}>
                                                                    <TableCell className="font-medium">{project.name}</TableCell>
                                                                    <TableCell>{project.projectType}</TableCell>
                                                                    <TableCell>
                                                                        <span className={`px-2 py-1 rounded-full text-sm ${getStatusBadgeClass(project.status)}`}>
                                                                            {project.status === 'submitted' ? 'Submitted' : 'In Progress'}
                                                                        </span>
                                                                    </TableCell>
                                                                    <TableCell>{project.createdAt ? formatDate(project.createdAt) : '-'}</TableCell>
                                                                    <TableCell>{project.updatedAt ? formatDate(project.updatedAt) : '-'}</TableCell>
                                                                    <TableCell className="text-right">
                                                                        <div className="flex justify-end gap-2">
                                                                            <Button
                                                                                onClick={() => handleDeleteProject(collection.id, project.id)}
                                                                                variant="destructive"
                                                                                size="sm"
                                                                            >
                                                                                <Trash2 className="h-4 w-4" />
                                                                            </Button>
                                                                        </div>
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            ))}
                        </TableBody>
                    </Table>
                </div>

                <div className="mt-6 text-center">
                    <Button
                        onClick={() => navigate('/projects')}
                        size="lg"
                        className="bg-blue-900 hover:bg-blue-800 text-white px-6 py-3"
                    >
                        Back to My Projects
                    </Button>
                </div>
            </main>
        </div>
    );
};

export default AdminProjects;